import type { MetricSeriesSpec } from '@/components/charts/generic/seriesTypes';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import { graphSettings } from '@/config/settings';
import type { HealthScoreMetrics } from '@/domain/health';
import { formatPercentageByLocale, type Locale } from '@/utils/localeFormatters';

import { toHealthPercent } from '../../_utils/healthDetailOverviewHelpers';

export const HEALTH_STATUS_SCORE_KEYS = ['score_1', 'score_2', 'score_3'] as const;

type ScoreKey = (typeof HEALTH_STATUS_SCORE_KEYS)[number];

const METRICS_SCORE_KEYS: Record<
    ScoreKey,
    keyof Pick<HealthScoreMetrics, 'score1' | 'score2' | 'score3'>
> = {
    score_1: 'score1',
    score_2: 'score2',
    score_3: 'score3',
};

export const HEALTH_STATUS_SCORE_COLORS: Record<ScoreKey, string> = {
    score_1: graphSettings.laksvel.score_1,
    score_2: graphSettings.laksvel.score_2,
    score_3: graphSettings.laksvel.score_3,
};

const STATUS_Y_STEP = 25;
const REMAINDER_OPACITY = 0.2;

/** Rounded top edge of the solid score segment, where it meets the faded remainder. */
const SCORE_TOP_CORNERS = { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 };

function scorePercent(metrics: HealthScoreMetrics | null | undefined, key: ScoreKey): number {
    return toHealthPercent(metrics?.[METRICS_SCORE_KEYS[key]]) ?? 0;
}

function percentLabel(value: number): string {
    return value === 0 ? '0 %' : `${Math.round(value)} %`;
}

/**
 * One stacked 0–100 % column per score: the solid share of fish with that score, labelled on
 * the bar, under a faded remainder to 100 %. The labels carry the values, so the chart has no
 * tooltip or legend, and nothing to zoom.
 *
 * The scores are one series coloured per bar rather than a series per score, so the CSV is a
 * single value column with the score as the row key. `scoreLabels` are the translated column
 * names, in score order, and the x keys; `valueLabel` heads the CSV's value column.
 */
export function buildHealthStatusChartSpec(
    metrics: HealthScoreMetrics | null | undefined,
    locale: Locale,
    scoreLabels: readonly string[],
    valueLabel: string
): MetricChartSpec<'bar'> {
    const values = HEALTH_STATUS_SCORE_KEYS.map((key) => scorePercent(metrics, key));
    const colors = HEALTH_STATUS_SCORE_KEYS.map((key) => HEALTH_STATUS_SCORE_COLORS[key]);
    const data = (valueAt: (value: number) => number) =>
        scoreLabels.map((column, index) => ({ day: column, value: valueAt(values[index]) }));

    const series: MetricSeriesSpec[] = [
        {
            key: 'score',
            label: valueLabel,
            color: colors[0],
            barColors: colors,
            showInLegend: false,
            borderRadius: SCORE_TOP_CORNERS,
            data: data((value) => value),
            barLabels: values.map(percentLabel),
        },
        {
            key: 'remainder',
            label: valueLabel,
            color: colors[0],
            barColors: colors,
            showInLegend: false,
            excludeFromCsv: true,
            barOpacity: REMAINDER_OPACITY,
            data: data((value) => Math.max(0, 100 - value)),
        },
    ];

    return {
        kind: 'bar',
        days: [...scoreLabels],
        xAxisKind: 'category',
        series,
        left: {
            label: '',
            min: 0,
            max: 100,
            stepSize: STATUS_Y_STEP,
            stacked: true,
            format: (value) => formatPercentageByLocale(value, locale, 0),
        },
        locale,
        tooltip: false,
        zoom: false,
    };
}
